"use client"

import {
  IconAlertTriangle,
  IconBrandWhatsapp,
  IconMicrophone,
  IconMicrophoneOff,
  IconPhoneOff,
} from "@tabler/icons-react"
import { useTranslations } from "next-intl"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface WhatsAppInCallBarProps {
  /** Lead name when known, otherwise the raw number. */
  callee: string | null
  /** True while dialing (before the media socket reports `call_started`). */
  dialing: boolean
  /**
   * True once the callee's phone has the offer. With a gateway that narrates
   * the call this is the real signal; with an older one the provider sets it
   * as soon as the media socket is up (the only thing it knows).
   */
  ringing: boolean
  /**
   * True once the far end picked up. Until then the bar says "Calling" or
   * "Ringing" instead of pretending the call is running, and the timer waits.
   */
  answered: boolean
  durationSec: number
  muted: boolean
  /** 0..1 peak of the frames actually being transmitted. */
  micLevel: number
  /** No mic signal at all for 3 s — almost always an OS/browser permission. */
  micSilent: boolean
  onToggleMute: () => void
  onHangup: () => void
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
}

/**
 * Floating in-call bar for an active WhatsApp call — mirrors the geometry and
 * control sizing of `telephony/in-call-controls.tsx` (fixed bottom-right card,
 * h-9 icon buttons) minus hold/transfer/notes, which waxum has no equivalent
 * for. Mute keeps the 60 ms frame cadence (silence, not a gap) inside the
 * bridge; hangup closes the media WS, which is the hangup signal.
 *
 * The mic meter is fed from the exact PCM frames the bridge transmits, so a
 * moving bar proves audio is reaching the sidecar — an AnalyserNode tapped off
 * the input can read healthy while the transmitted leg is dead.
 */
export function WhatsAppInCallBar({
  callee,
  dialing,
  ringing: ringingProp,
  answered,
  durationSec,
  muted,
  micLevel,
  micSilent,
  onToggleMute,
  onHangup,
}: WhatsAppInCallBarProps) {
  const t = useTranslations("whatsappCalls")
  const ringing = !dialing && !answered && ringingProp

  return (
    <div className="fixed bottom-6 right-6 z-50 w-80 rounded-xl border bg-background p-4 shadow-lg">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex min-w-0 items-center gap-2">
            <IconBrandWhatsapp className="h-4 w-4 shrink-0 text-[#128C7E] dark:text-[#25D366]" />
            <div className="min-w-0">
              <p
                className={cn(
                  "text-xs text-muted-foreground",
                  !answered && !dialing && "animate-pulse",
                  ringing && "text-amber-600 dark:text-amber-500"
                )}
              >
                {answered
                  ? t("inCall.inCall")
                  : ringing
                    ? t("inCall.ringing")
                    : t("inCall.calling")}
              </p>
              <p className="truncate text-sm font-medium">
                {callee || t("inCall.activeCall")}
              </p>
            </div>
          </div>
          {answered && (
            <p className="text-xs tabular-nums text-muted-foreground">
              {formatElapsed(durationSec)}
            </p>
          )}
        </div>

        {/* Meter only once the mic is actually up and transmitting. */}
        {!dialing && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <IconMicrophone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                <div
                  className={cn(
                    "h-full rounded-full transition-[width] duration-100",
                    micSilent ? "bg-red-500" : muted ? "bg-muted-foreground/40" : "bg-emerald-500"
                  )}
                  style={{ width: `${Math.max(2, Math.round(micLevel * 100))}%` }}
                />
              </div>
            </div>
            {micSilent && !muted && (
              <p className="flex items-start gap-1.5 rounded-md bg-red-50 px-2 py-1.5 text-[11px] text-red-700 dark:bg-red-950/40 dark:text-red-300">
                <IconAlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
                <span>{t("inCall.micSilent")}</span>
              </p>
            )}
          </div>
        )}

        <div className="flex items-center justify-center gap-2">
          {/* Mute */}
          <Button
            variant={muted ? "default" : "outline"}
            size="icon"
            className={`h-9 w-9 ${muted ? "bg-red-500 text-white hover:bg-red-600" : ""}`}
            onClick={onToggleMute}
            disabled={dialing}
            aria-label={muted ? t("inCall.unmute") : t("inCall.mute")}
          >
            {muted ? (
              <IconMicrophoneOff className="h-4 w-4" />
            ) : (
              <IconMicrophone className="h-4 w-4" />
            )}
          </Button>

          {/* Hang up */}
          <Button
            variant="destructive"
            size="icon"
            className="h-9 w-9"
            onClick={onHangup}
            aria-label={t("inCall.hangUp")}
          >
            <IconPhoneOff className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
